// src/components/ActionAreaStatusRow.js

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Form, Dropdown } from 'react-bootstrap';
import { IconPencil, IconChevronDown } from '@tabler/icons-react';
import { useSelector, useDispatch } from 'react-redux';
import { saveActionArea } from '../features/actionAreasSlice';
import { updateCheckpointStatusLocally } from '../features/checkpointsSlice';
import ActionAreaOffcanvas from './offcanvas/ActionAreaOffcanvas';
import './ActionAreaStatusRow.css';

// Status configuration
const STATUS_OPTIONS = [
  { value: '', label: 'Kein Status' },
  { value: 'nicht erreicht', label: 'nicht erreicht' },
  { value: 'erreicht', label: 'erreicht' },
  { value: 'mehr als erreicht', label: 'mehr als erreicht' },
];

const getActionAreaStatusClass = (status) => {
  switch (status) {
    case 'nicht erreicht':
      return 'status-not-achieved';
    case 'erreicht':
      return 'status-achieved';
    case 'mehr als erreicht':
      return 'status-overachieved';
    default:
      return 'status-no-status';
  }
};

// Single Action Area Item with integrated status
const ActionAreaStatusItem = ({
  actionArea,
  actionAreaIndex,
  unitId,
  checkpointId,
  readOnly,
  isLast = false,
}) => {
  const dispatch = useDispatch();
  
  // Get action area data from Redux store
  const actionAreaFromStore = useSelector(state =>
    state.actionAreas.entities[actionArea.id]
  ) || actionArea;
  
  // Local states
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [openToResults, setOpenToResults] = useState(false);
  const [localComment, setLocalComment] = useState('');
  const [localStatus, setLocalStatus] = useState('');
  
  // Formatted action area data
  const formattedActionArea = useMemo(() => {
    const sourceData = {
      ...actionArea,
      ...actionAreaFromStore,
      status_id: actionAreaFromStore?.status_id ?? actionArea?.status_id,
      status_description: actionAreaFromStore?.status_description ?? actionArea?.status_description,
      unit: actionAreaFromStore?.unit ?? actionArea?.unit ?? unitId,
    };

    return {
      id: sourceData.id,
      actionarea_title: sourceData.actionarea_title || "",
      measures: sourceData.measures || [],
      tags: sourceData.tags || [],
      description: sourceData.description || '',
      examples: sourceData.examples || '',
      priority: sourceData.priority || 'Niedrig',
      goal: sourceData.goal || '',
      fixed_goal: sourceData.fixed_goal || '',
      measure: sourceData.measure || '',
      ends_at: sourceData.ends_at || null,
      comment: sourceData.comment || '',
      learning1: sourceData.learning1 || '',
      learning2: sourceData.learning2 || '',
      learning3: sourceData.learning3 || '',
      status: sourceData.status || '',
      status_description: sourceData.status_description || '',
      result: sourceData.result || '',
      unit: sourceData.unit || unitId,
      status_id: sourceData.status_id,
    };
  }, [actionArea, actionAreaFromStore, unitId]);
  
  const [editableActionArea, setEditableActionArea] = useState(formattedActionArea);
  
  // Sync local data when Redux data changes
  useEffect(() => {
    setLocalComment(formattedActionArea.comment || '');
    setLocalStatus(formattedActionArea.status || '');
    setEditableActionArea(formattedActionArea);
  }, [formattedActionArea]);
  
  // Handle status change - auto-save on change
  const handleStatusChange = (newStatus) => {
    if (readOnly) return;
    setLocalStatus(newStatus);
    
    // Auto-save status change
    const updatedData = {
      ...editableActionArea,
      comment: localComment,
      status: newStatus,
    };
    setEditableActionArea(updatedData);
    if (formattedActionArea.status_id) {
      dispatch(
        updateCheckpointStatusLocally({
          statusId: formattedActionArea.status_id,
          newStatus,
        })
      );
    }
    dispatch(saveActionArea(updatedData));
  };
  
  // Handle comment change
  const handleCommentChange = (e) => {
    if (readOnly) return;
    setLocalComment(e.target.value);
  };
  
  // Handle comment blur - auto-save on blur
  const handleCommentBlur = () => {
    if (readOnly) return;
    if (localComment !== (formattedActionArea.comment || '')) {
      const updatedData = {
        ...editableActionArea,
        comment: localComment,
        status: localStatus,
      };
      setEditableActionArea(updatedData);
      dispatch(saveActionArea(updatedData));
    }
  };
  
  // Handle data change from offcanvas
  const handleDataChangeFromOffcanvas = useCallback((update) => {
    setEditableActionArea(prev => ({
      ...prev,
      ...update,
    }));
  }, []);
  
  // Handle offcanvas close and save
  const handleCloseOffcanvas = useCallback(() => {
    setShowOffcanvas(false);
    setOpenToResults(false);
    
    if (JSON.stringify(editableActionArea) !== JSON.stringify(formattedActionArea)) {
      if (
        formattedActionArea.status_id &&
        editableActionArea.status !== formattedActionArea.status
      ) {
        dispatch(
          updateCheckpointStatusLocally({
            statusId: formattedActionArea.status_id,
            newStatus: editableActionArea.status || '',
          })
        );
      }
      dispatch(saveActionArea(editableActionArea));
    }
  }, [dispatch, editableActionArea, formattedActionArea]);
  
  // Handle opening offcanvas (to view goals)
  const handleOpenDetails = (openToGoals = false) => {
    setOpenToResults(openToGoals);
    setShowOffcanvas(true);
  };
  
  // Get current status label for dropdown
  const getCurrentStatusLabel = () => {
    const option = STATUS_OPTIONS.find(opt => opt.value === localStatus);
    return option ? option.label : 'Status wählen';
  };
  
  return (
    <div className="aa-checkpoint-item">
      {/* Header Row: Title + Question + Status */}
      <div className="aa-checkpoint-header">
        {/* Left side: Title as link with edit icon */}
        <button 
          className="aa-checkpoint-title-btn"
          onClick={handleOpenDetails}
          type="button"
        >
          <span className="aa-checkpoint-title-text">
            {formattedActionArea.actionarea_title || 'Handlungsfeld ohne Titel'}
          </span>
          {!readOnly && (
            <IconPencil size={16} stroke={2} className="aa-checkpoint-edit-icon" />
          )}
        </button>
        
        {/* Middle: Question text and link */}
        <div className="aa-checkpoint-question-section">
          <p className="aa-checkpoint-question-text">
            Wurde das gesetzte Ziel in diesem Handlungsfeld erreicht?
          </p>
          <button 
            className="aa-checkpoint-goals-link"
            onClick={() => handleOpenDetails(true)}
            type="button"
          >
            Finde die Ziele in deinem Handlungsfeld
          </button>
        </div>
        
        {/* Right side: Status dropdown */}
        <div className="aa-checkpoint-status-section">
          {readOnly ? (
            <div className={`aa-checkpoint-status-readonly ${getActionAreaStatusClass(localStatus)}`}>
              {getCurrentStatusLabel()}
            </div>
          ) : (
            <Dropdown>
              <Dropdown.Toggle 
                className={`aa-checkpoint-status-dropdown ${getActionAreaStatusClass(localStatus)}`}
                id={`status-dropdown-${formattedActionArea.id}`}
              >
                <span>{getCurrentStatusLabel()}</span>
                <IconChevronDown size={16} stroke={2} />
              </Dropdown.Toggle>
              
              <Dropdown.Menu className="aa-checkpoint-status-menu">
                {STATUS_OPTIONS.map((option) => (
                  <Dropdown.Item
                    key={option.value}
                    onClick={() => handleStatusChange(option.value)}
                    active={localStatus === option.value}
                    className="aa-checkpoint-status-option"
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
      
      {/* Comment Row */}
      <div className="aa-checkpoint-comment-section">
        <Form.Control
          as="textarea"
          rows={4}
          placeholder="Kommentarfeld"
          value={localComment}
          onChange={handleCommentChange}
          onBlur={handleCommentBlur}
          readOnly={readOnly}
          className="aa-checkpoint-comment-input"
        />
      </div>
      
      {/* Action Area Offcanvas */}
      {showOffcanvas && (
        <ActionAreaOffcanvas
          showOffcanvas={showOffcanvas}
          handleCloseOffcanvas={handleCloseOffcanvas}
          actionAreaData={editableActionArea}
          handleActionAreaDataChange={handleDataChangeFromOffcanvas}
          isUnitLeader={!readOnly}
          openToResults={openToResults}
        />
      )}

      {!isLast && <hr />}
    </div>

    
  );
};

const ActionAreaStatusRow = ({
  checkpointIndex,
  actionAreaIndex,
  checkpointId,
  actionArea,
  unitId,
  hideHeaders = false,
  readOnly = false,
  isLast = false,
}) => {
  if (!actionArea) {
    return (
      <div className="aa-checkpoint-empty">
        Kein Handlungsfeld verfügbar.
      </div>
    );
  }
  
  return (
    <div className="aa-checkpoint-row-container">
      <ActionAreaStatusItem
        actionArea={actionArea}
        actionAreaIndex={actionAreaIndex}
        unitId={unitId}
        checkpointId={checkpointId}
        readOnly={readOnly}
        isLast={isLast}
      />
    </div>
  );
};

export default ActionAreaStatusRow;
